/**
 * 错误边界组件
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isStaleAssetError, reloadForFreshAssets } from '@/utils/staleAssetReload';
import { Result, Button, Alert, Card, Typography, Collapse, Space, Tag } from 'antd';
import { 
  BugOutlined, 
  ReloadOutlined, 
  HomeOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableRetry?: boolean;
  maxRetries?: number;
  showErrorDetails?: boolean;
  level?: 'page' | 'component' | 'widget';
  reportError?: (error: Error, errorInfo: ErrorInfo, errorId: string) => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Auto-reload on stale chunk errors (happens after deploy when browser has old HTML)
    if (isStaleAssetError(error)) {
      reloadForFreshAssets();
      return;
    }

    const { onError, reportError } = this.props;
    const { errorId } = this.state;

    this.setState({ errorInfo });
    onError?.(error, errorInfo);
    reportError?.(error, errorInfo, errorId);

    console.group('🚨 React Error Boundary');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    this.logErrorToStorage(error, errorInfo, errorId);
  }

  private logErrorToStorage = (error: Error, errorInfo: ErrorInfo, errorId: string) => {
    try {
      const errorLog = {
        id: errorId,
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.getCurrentUserId(),
        level: this.props.level || 'component'
      };

      const existingLogs = JSON.parse(localStorage.getItem('error_logs') || '[]');
      existingLogs.push(errorLog);
      
      const recentLogs = existingLogs.slice(-50);
      localStorage.setItem('error_logs', JSON.stringify(recentLogs));
    } catch (storageError) {
      console.warn('Failed to log error to storage:', storageError);
    }
  };

  private getCurrentUserId = (): string => {
    try {
      return localStorage.getItem('user_id') || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  private handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    }
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReload = () => {
    window.location.reload();
  };

  private copyErrorInfo = () => {
    const { error, errorInfo, errorId } = this.state;
    const errorText = `
错误ID: ${errorId}
时间: ${new Date().toLocaleString()}
错误信息: ${error?.message}
错误堆栈: ${error?.stack}
组件堆栈: ${errorInfo?.componentStack}
页面URL: ${window.location.href}
用户代理: ${navigator.userAgent}
`;

    navigator.clipboard.writeText(errorText).then(() => {
      console.log('错误信息已复制到剪贴板');
    });
  };

  render() {
    const { 
      hasError, 
      error, 
      errorInfo, 
      errorId, 
      retryCount 
    } = this.state;
    
    const { 
      children, 
      fallback, 
      enableRetry = true, 
      maxRetries = 3,
      showErrorDetails = true,
      level = 'component'
    } = this.props;

    if (hasError) {
      if (fallback) {
        return <>{fallback}</>;
      }

      if (level === 'widget') {
        return (
          <Alert
            message="组件异常"
            description="部分功能暂时不可用，但不影响其他操作"
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            action={
              enableRetry && retryCount < maxRetries ? (
                <Button size="small" onClick={this.handleRetry}>
                  重试 ({retryCount + 1}/{maxRetries + 1})
                </Button>
              ) : undefined
            }
            style={{ margin: '8px 0' }}
          />
        );
      }

      return (
        <div style={{ padding: '24px', minHeight: '400px' }}>
          <Result
            status="error"
            title="组件渲染失败"
            subTitle="组件渲染时发生错误，请尝试重新加载"
            icon={<BugOutlined />}
            extra={
              <Space direction="vertical" align="center">
                <Space wrap>
                  {enableRetry && retryCount < maxRetries && (
                    <Button 
                      type="primary" 
                      icon={<ReloadOutlined />}
                      onClick={this.handleRetry}
                    >
                      重试 ({retryCount + 1}/{maxRetries + 1})
                    </Button>
                  )}
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={this.handleReload}
                  >
                    刷新页面
                  </Button>
                  <Button 
                    icon={<HomeOutlined />}
                    onClick={this.handleGoHome}
                  >
                    返回首页
                  </Button>
                </Space>

                {showErrorDetails && (
                  <Card 
                    title="错误详情" 
                    size="small" 
                    style={{ width: '100%', maxWidth: '600px', textAlign: 'left' }}
                    extra={
                      <Button 
                        size="small" 
                        icon={<CopyOutlined />}
                        onClick={this.copyErrorInfo}
                      >
                        复制错误信息
                      </Button>
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>错误ID：</Text>
                        <Tag>{errorId}</Tag>
                      </div>
                      
                      <div>
                        <Text strong>发生时间：</Text>
                        <Text>{new Date().toLocaleString()}</Text>
                      </div>

                      {error && (
                        <Collapse size="small" ghost>
                          <Panel 
                            header={
                              <span>
                                <InfoCircleOutlined style={{ marginRight: 8 }} />
                                技术详情
                              </span>
                            } 
                            key="1"
                          >
                            <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                              <div style={{ marginBottom: '8px' }}>
                                <Text strong>错误信息：</Text>
                                <Paragraph 
                                  copyable={{ text: error.message }}
                                  style={{ 
                                    background: '#f5f5f5', 
                                    padding: '8px', 
                                    marginTop: '4px',
                                    fontSize: '12px',
                                    fontFamily: 'monospace'
                                  }}
                                >
                                  {error.message}
                                </Paragraph>
                              </div>

                              {error.stack && (
                                <div style={{ marginBottom: '8px' }}>
                                  <Text strong>错误堆栈：</Text>
                                  <Paragraph 
                                    copyable={{ text: error.stack }}
                                    style={{ 
                                      background: '#f5f5f5', 
                                      padding: '8px', 
                                      marginTop: '4px',
                                      fontSize: '11px',
                                      fontFamily: 'monospace',
                                      maxHeight: '200px',
                                      overflow: 'auto'
                                    }}
                                  >
                                    {error.stack}
                                  </Paragraph>
                                </div>
                              )}
                            </div>
                          </Panel>
                        </Collapse>
                      )}
                    </Space>
                  </Card>
                )}
              </Space>
            }
          />
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;